'use client';

import { useCallback, useState, type SetStateAction } from 'react';
import { useTranslations } from 'next-intl';
import { FlaskConical, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { enforcePdfFileLimit } from '@/lib/pdf-upload-limit';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { FileUploader } from '@/components/file-uploader';
import { UploadProgress } from '@/components/upload-progress';
import { InfiniteCombobox } from '@/components/forms/infinite-combobox';
import { useInfiniteSelect } from '@/hooks/use-infinite-select';
import { useUploadProgress } from '@/hooks/use-upload-progress';
import {
  ocrTemplateKeys,
  ocrTemplatesApi,
  TemplateFieldsInfo,
  type OcrTemplate
} from '@/features/ocr-templates';
import { useMaxPdfUploadFiles } from '@/features/configurations';
import { getPdfPageCount, renderPdfToImages } from '@/features/photo-scanning';
import {
  useAppendTestContainerPagesMutation,
  useCreateTestContainerMutation
} from '../services/ocr.mutations';

/** Backend caps a container at 500 pages; rasterized PDFs are sliced to match. */
const MAX_PAGES = 500;
/**
 * Each upload request carries at most this many images, to stay under PHP's
 * `max_file_uploads` ceiling (default 20). Large documents are streamed across
 * several requests. Mirrors the photo-scanning chunk size.
 */
const CHUNK_SIZE = 15;
/** Max size of a *source* file (a PDF can be hundreds of pages / MB). */
const MAX_SOURCE_FILE_BYTES = 1024 * 1024 * 300;

interface CreateTestContainerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateTestContainerDialog({
  open,
  onOpenChange
}: CreateTestContainerDialogProps) {
  const t = useTranslations('ocr');
  const tCommon = useTranslations('common');
  const createMutation = useCreateTestContainerMutation();
  const appendMutation = useAppendTestContainerPagesMutation();
  const maxPdfFiles = useMaxPdfUploadFiles();
  // One smooth 0→100 bar, byte-weighted across every chunk upload.
  const { progress, onProgress, reset: resetProgress } = useUploadProgress();

  const [files, setFiles] = useState<File[]>([]);
  const [entityName, setEntityName] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [processing, setProcessing] = useState(false);
  // Upload progress for large documents: pages sent / pages total.
  const [uploaded, setUploaded] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Active templates for the optional default picker — infinite scroll + server
  // search, so the list is never capped. Mirrors the "Assign per page" picker.
  const templatesSelect = useInfiniteSelect<OcrTemplate>({
    queryKey: ocrTemplateKeys.lists(),
    queryFn: (params) => ocrTemplatesApi.getAll(params),
    mapOption: useCallback(
      (tpl: OcrTemplate) => ({ value: String(tpl.id), label: tpl.name }),
      []
    ),
    params: { is_active: true }
  });

  function reset() {
    setFiles([]);
    setEntityName('');
    setTemplateId('');
    setUploaded(0);
    setTotalPages(0);
  }

  function handleOpenChange(next: boolean) {
    if (next || !processing) {
      if (!next) reset();
      onOpenChange(next);
    }
  }

  const busy =
    processing || createMutation.isPending || appendMutation.isPending;

  /**
   * One picked file is one source document — a PDF arrives as a single file and
   * is rasterized into pages client-side. Past one, the container has no single
   * document to be named after: the server would fall back to joining every
   * file name into one string, which reads as `a.pdf, b.pdf` in the list. So the
   * optional label stops being optional and becomes the container's name.
   */
  const labelRequired = files.length > 1;
  const hasLabel = entityName.trim() !== '';
  const canCreate =
    files.length > 0 &&
    templateId !== '' &&
    (!labelRequired || hasLabel) &&
    !busy;

  /**
   * Selection gate for the global PDF-file limit (`max_pdf_upload_files`,
   * 0 = unlimited). Derived from the current selection, so removing a PDF
   * via the file card frees its slot automatically.
   */
  function handleFilesChange(next: SetStateAction<File[]>) {
    const resolved = typeof next === 'function' ? next(files) : next;
    const { accepted, removedCount } = enforcePdfFileLimit(
      resolved,
      maxPdfFiles
    );
    if (removedCount > 0) {
      toast.error(tCommon('maxPdfFilesExceeded', { max: maxPdfFiles }));
    }
    setFiles(accepted);
  }

  /** Total pages across the picked files (rasterizing PDFs), capped at MAX. */
  async function countTotalPages(): Promise<number> {
    let total = 0;
    for (const file of files) {
      if (total >= MAX_PAGES) break;
      total +=
        file.type === 'application/pdf' ? await getPdfPageCount(file) : 1;
    }
    return Math.min(total, MAX_PAGES);
  }

  /**
   * Rasterize + upload the picked files as a stream of ≤CHUNK_SIZE-image
   * requests: the first chunk creates the container, the rest are appended.
   * Pages are rasterized one at a time and uploaded per chunk, so a large PDF
   * never holds hundreds of blobs in memory at once. The progress bar is
   * page-count-weighted (completed pages + the in-flight chunk's fraction, over
   * the known total) into one smooth 0→100 — no need to size every blob up front.
   */
  async function handleCreate() {
    if (!canCreate) return;
    setProcessing(true);
    setUploaded(0);
    try {
      const total = await countTotalPages();
      if (total === 0) {
        toast.error(t('testNeedFile'));
        return;
      }
      setTotalPages(total);

      let containerId: number | null = null;
      let sent = 0;
      let produced = 0;
      let buffer: File[] = [];

      const flush = async () => {
        if (buffer.length === 0) return;
        const chunk = buffer;
        buffer = [];

        // Page-count-weighted progress: pages already uploaded + this chunk's
        // in-flight fraction, over the known total. Streams per chunk (never
        // holds every blob in memory) while showing one smooth 0→100.
        const completedPages = sent;
        const chunkPages = chunk.length;
        const onChunkProgress = (chunkPct: number) =>
          onProgress(
            Math.round(
              ((completedPages + (chunkPages * chunkPct) / 100) / total) * 100
            )
          );

        const formData = new FormData();
        chunk.forEach((img) => formData.append('images[]', img));

        if (containerId === null) {
          if (entityName.trim() !== '') {
            formData.append('entity_name', entityName.trim());
          }
          formData.append('ocr_template_id', templateId);
          const created = await createMutation.mutateAsync({
            formData,
            onProgress: onChunkProgress
          });
          containerId = created.data.id;
        } else {
          await appendMutation.mutateAsync({
            containerId,
            formData,
            onProgress: onChunkProgress
          });
        }

        sent += chunk.length;
        setUploaded(sent);
      };

      const push = async (img: File) => {
        buffer.push(img);
        if (buffer.length >= CHUNK_SIZE) await flush();
      };

      for (const file of files) {
        if (produced >= total) break;
        if (file.type === 'application/pdf') {
          await renderPdfToImages(file, {
            maxPages: total - produced,
            mimeType: 'image/jpeg',
            onPage: async (page) => {
              if (produced >= total) return;
              produced++;
              await push(
                new File([page.blob], `${file.name}-p${page.pageNumber}.jpg`, {
                  type: 'image/jpeg'
                })
              );
            }
          });
        } else {
          produced++;
          await push(file);
        }
      }
      await flush();

      reset();
      onOpenChange(false);
    } catch {
      // surfaced by the global MutationCache toast
    } finally {
      setProcessing(false);
      resetProgress();
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <FlaskConical className='h-5 w-5' />
            {t('testContainerTitle')}
          </DialogTitle>
          <DialogDescription>{t('testContainerSubtitle')}</DialogDescription>
        </DialogHeader>

        <div className='space-y-4'>
          <div className='space-y-1.5'>
            <Label htmlFor='test-entity-name'>
              {labelRequired
                ? t('testEntityNameRequiredLabel')
                : t('testEntityNameLabel')}
              {labelRequired && <span className='ms-1 text-red-500'>*</span>}
            </Label>
            <Input
              id='test-entity-name'
              value={entityName}
              onChange={(e) => setEntityName(e.target.value)}
              placeholder={
                labelRequired
                  ? t('testEntityNamePlaceholderMulti')
                  : t('testEntityNamePlaceholder')
              }
              maxLength={120}
              disabled={busy}
            />
            {labelRequired && !hasLabel && (
              <p className='text-muted-foreground text-xs'>
                {t('testEntityNameMultiFileHint', { count: files.length })}
              </p>
            )}
          </div>

          <div className='space-y-1.5'>
            <Label>
              {t('testDefaultTemplateLabel')}
              <span className='ms-1 text-red-500'>*</span>
            </Label>
            <InfiniteCombobox
              value={templateId}
              onChange={setTemplateId}
              infiniteConfig={templatesSelect}
              placeholder={t('testDefaultTemplatePlaceholder')}
              searchPlaceholder={t('searchTemplates')}
              emptyMessage={t('noTemplates')}
              disabled={busy}
              className='w-full'
              renderOptionAdornment={(option) => (
                <TemplateFieldsInfo templateId={Number(option.value)} />
              )}
            />
            <p className='text-muted-foreground text-xs'>
              {templateId === ''
                ? t('testDefaultTemplateRequired')
                : t('testDefaultTemplateHint')}
            </p>
          </div>

          <div className='space-y-1.5'>
            <Label>{t('testUploadHint')}</Label>
            <FileUploader
              value={files}
              onValueChange={handleFilesChange}
              accept={{ 'image/*': [], 'application/pdf': [] }}
              maxFiles={MAX_PAGES}
              maxSize={MAX_SOURCE_FILE_BYTES}
              multiple
              disabled={busy}
            />
          </div>

          {processing && totalPages > 0 ? (
            <div className='space-y-1.5'>
              <p
                className='text-muted-foreground text-xs'
                role='status'
                aria-live='polite'
              >
                {t('testUploadingPages', {
                  current: uploaded,
                  total: totalPages
                })}
              </p>
              <UploadProgress value={progress} />
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => handleOpenChange(false)}
            disabled={busy}
          >
            {t('cancel')}
          </Button>
          <Button onClick={handleCreate} disabled={!canCreate}>
            <span className={cn('flex items-center', busy && 'opacity-90')}>
              {busy ? (
                <Loader2 className='me-2 h-4 w-4 animate-spin' />
              ) : (
                <FlaskConical className='me-2 h-4 w-4' />
              )}
              {processing ? t('testProcessing') : t('createTestContainer')}
            </span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
